import PilotBadge from "@/components/PilotBadge";
import {
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarProvider,
  SidebarGroupContent,
  SidebarInset,
  SidebarMenuButton,
} from "@/components/ui/sidebar";
import useAppLinks from "@/lib/hooks/useAppLinks";
import { useMediaQuery } from "@/lib/hooks/useMediaQuery";
import { Feature } from "@/lib/queries/user/useInit";
import { cn } from "@/lib/utils";
import { Key, PaintBrush, User, Translate } from "@phosphor-icons/react";
import { useStytchSession } from "@stytch/react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface AppLink {
  id: string;
  href: string;
  title: string;
  icon: React.ComponentType;
  end: boolean;
  active?: boolean;
  isPilot?: boolean;
}

function SettingsLayout() {
  const { t } = useTranslation("settings");
  const stytchSession = useStytchSession();
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const { features, isPilot } = useAppLinks();
  if (!stytchSession.session) {
    // Capture the current URL and pass it as returnTo parameter
    const returnTo = encodeURIComponent(location.pathname + location.search);
    navigate(`/authenticate?returnTo=${returnTo}`);
    return null;
  }

  const links: AppLink[] = [
    {
      id: "1",
      href: "/settings",
      title: t("navigation.profile"),
      icon: User,
      end: true,
      active: true,
    },
    {
      id: "2",
      href: "/settings/subscription",
      title: t("navigation.subscriptionBilling"),
      icon: Key,
      end: false,
      active: true,
    },
  ];

  let settingsLinks: AppLink[] = [...links];

  // Add language completion link only in development mode
  if (import.meta.env.DEV) {
    settingsLinks.push({
      id: "language-completion",
      href: "/settings/language-completion",
      title: t("navigation.languageCompletion"),
      icon: Translate,
      end: false,
      active: true,
    });
  }

  settingsLinks = [
    ...settingsLinks,
    ...(features
      ?.filter((f: Feature) => f.active)
      ?.map((feature: Feature) => ({
        id: (settingsLinks.length + 1).toString(),
        href: `/settings/${feature.name.toLowerCase().replace(/\s+/g, "-")}`,
        title: t(`navigation.${feature.name.toLowerCase()}`),
        icon: PaintBrush,
        end: false,
        active: true,
        isPilot: feature.isPilot,
      })) || []),
  ];

  return (
    <div className="flex flex-row w-full gap-4 min-h-full">
      {isDesktop && (
        <SidebarProvider>
          <SidebarInset>
            <SidebarContent>
              <SidebarGroup>
                <SidebarGroupLabel className="mt-10 mb-4 text-4xl font-semibold text-foreground">
                  {t("layout.title")}
                </SidebarGroupLabel>
                <SidebarGroupContent className="flex flex-col gap-2 mt-28">
                  {settingsLinks
                    .filter((f) => f.active === true)
                    .map((link) => {
                      return (
                        <NavLink to={link.href} end={link.end} key={link.id}>
                          {({ isActive }) => (
                            <SidebarMenuButton
                              className={cn(
                                "text-lg h-fit w-full font-semibold whitespace-nowrap ",
                                isActive
                                  ? "bg-secondary text-green-950 hover:bg-secondary/70 hover:text-green-950 border border-result text-secondary-foreground"
                                  : "text-foreground border bg-very-light border-result"
                              )}
                            >
                              {link.title}
                              {link.isPilot && isPilot && (
                                <PilotBadge variant={"small"} />
                              )}
                            </SidebarMenuButton>
                          )}
                        </NavLink>
                      );
                    })}
                </SidebarGroupContent>
              </SidebarGroup>
            </SidebarContent>
          </SidebarInset>
        </SidebarProvider>
      )}
      <Outlet />
    </div>
  );
}

export default SettingsLayout;
